import { useCallback, useEffect, useRef, useState } from 'react';
import { faGear, faPlus } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';

import { AddPageModal } from '@/components/page-editor/add-page-modal';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { DestructiveText, MutedText } from '@/components/ui/typography';
import { useDebounce } from '@/hooks/use-debounce';
import type { CollectionCardField } from '@/lib/blocks/collection/blocks';
import {
  type CollectionButtonSettings,
  DEFAULT_COLLECTION_BUTTON_SETTINGS,
  readCollectionButtonSettings,
} from '@/lib/blocks/collection/button-settings';
import {
  type DataWidgetBlockType,
  type DataWidgetFilterSelection,
  type DataWidgetSourceConfig,
  type DataWidgetStrategy,
  type DataWidgetVariant,
  getDataWidgetLabel,
  isDataWidgetBlockType,
  isDataWidgetMetadataSlot,
  resolveDataWidgetProps,
} from '@/lib/blocks/collection/data-widget/blocks';
import {
  getDataWidgetVariants,
  type LayoutVariantEntry,
} from '@/lib/blocks/collection/variant-registry';
import { resolveBlockProps } from '@/lib/blocks/prop-resolver';
import { cmsReviewsOverviewPath } from '@/lib/routes';
import { cn, openWindow } from '@/lib/utils';
import { usePageEditorStore } from '@/stores/page-editor-store';
import type { PageBlock } from '@/types/block';
import { PageType } from '@/types/page';
import { BackgroundTab } from './collection/settings/background-tab';
import { ButtonTab } from './collection/settings/button-tab';
import { ContentTab, type ContentTabFieldGroup } from './collection/settings/content-tab';
import type { MetadataFilterRow } from './collection/settings/content-tab-metadata-filter';
import { LayoutTab } from './collection/settings/layout-tab';
import { SettingsPopoverShell } from './collection/settings/settings-popover-shell';
import { SettingsTab } from './collection/settings/settings-tab';
import { useCollectionSettings } from './collection/settings/use-collection-settings';
import { BlockActionButton } from './toolbar/block-action-button';

interface DataWidgetSettingsDialogProps {
  sectionId: string;
  block: PageBlock;
  cardTemplateBlock?: PageBlock;
  cardTemplateChildren?: PageBlock[];
  isVisible: boolean;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
}

type WidgetCreationConfig = {
  createLabel: string;
  emptyTitle: string;
  emptyDescription: string;
  helperCopy: string;
} & (
  | { kind: 'page'; initialSidebarSelection: PageType; chainedChildPageType: PageType }
  | { kind: 'link'; href: string }
);

function getWidgetCreationConfig(widgetType: DataWidgetBlockType): WidgetCreationConfig {
  switch (widgetType) {
    case 'blog_widget':
      return {
        kind: 'page',
        createLabel: 'Add Blog Posts',
        emptyTitle: 'No Blog Posts exist yet.',
        emptyDescription: 'Add blog posts to enable this option.',
        helperCopy: 'Automatically display published blog posts',
        initialSidebarSelection: PageType.BLOG_INDEX,
        chainedChildPageType: PageType.BLOG,
      };
    case 'case_result_widget':
      return {
        kind: 'page',
        createLabel: 'Add Case Results',
        emptyTitle: 'No Case Results exist yet.',
        emptyDescription: 'Add case results to enable this option.',
        helperCopy: 'Automatically display published case results',
        initialSidebarSelection: PageType.CASE_RESULT_INDEX,
        chainedChildPageType: PageType.CASE_RESULT,
      };
    case 'review_widget':
      return {
        kind: 'link',
        createLabel: 'Add Reviews',
        emptyTitle: 'No Reviews exist yet.',
        emptyDescription: 'Add reviews to enable this option.',
        helperCopy: 'Automatically display published reviews',
        href: cmsReviewsOverviewPath(),
      };
  }
}

const DEFAULT_STRATEGY_OPTIONS: ReadonlyArray<{ value: DataWidgetStrategy; label: string }> = [
  { value: 'newest', label: 'Most recent' },
  { value: 'pinned_only', label: 'Pinned only' },
  { value: 'pinned_first', label: 'Pinned first' },
];

const REVIEW_STRATEGY_OPTIONS: ReadonlyArray<{ value: DataWidgetStrategy; label: string }> = [
  { value: 'newest', label: 'Most recent' },
  { value: 'sorted', label: 'Sorted' },
];

function getStrategyOptions(widgetType: DataWidgetBlockType) {
  return widgetType === 'review_widget' ? REVIEW_STRATEGY_OPTIONS : DEFAULT_STRATEGY_OPTIONS;
}

function getWidgetFieldGroups(widgetType: DataWidgetBlockType): ContentTabFieldGroup[] {
  switch (widgetType) {
    case 'blog_widget':
      return [{ slots: ['publishedAt', 'authors'] }];
    case 'review_widget':
      return [{ slots: ['reviewerIcon', 'reviewerName'] }];
    default:
      return [];
  }
}

// Reviews have no attorney card field, so their filter renders as a standalone row.
function getWidgetFilterConfig(widgetType: DataWidgetBlockType): {
  slots: string[];
  standaloneRows?: MetadataFilterRow[];
} {
  switch (widgetType) {
    case 'case_result_widget':
      return { slots: ['attorneys', 'court', 'practiceAreas'] };
    case 'blog_widget':
      return { slots: ['authors', 'topics'] };
    case 'review_widget':
      return { slots: [], standaloneRows: [{ slot: 'attorneys', label: 'Attorneys' }] };
  }
}

function sourceToProps(source: DataWidgetSourceConfig): Record<string, unknown> {
  return {
    mode: source.mode,
    strategy: source.strategy,
    limit: source.limit,
    filters: source.filters,
  };
}

export function DataWidgetSettingsDialog({
  sectionId,
  block,
  cardTemplateBlock,
  cardTemplateChildren = [],
  isVisible,
  disabled,
  onOpenChange,
}: DataWidgetSettingsDialogProps) {
  const batchUpdateBlocks = usePageEditorStore((state) => state.batchUpdateBlocks);
  const pageId = usePageEditorStore((state) => state.page?.id);
  const sectionTemplateCode = usePageEditorStore(
    (state) =>
      state.page?.slots?.main?.sections?.find((s) => s.id === sectionId)?.sectionTemplateCode,
  );
  const variants = getDataWidgetVariants() as LayoutVariantEntry[];

  const abortControllerRef = useRef<AbortController | null>(null);

  const settings = useCollectionSettings({
    sectionId,
    block,
    cardTemplateBlock,
    cardTemplateChildren,
    variants,
    onOpenChange: (nextOpen) => {
      if (!nextOpen) abortControllerRef.current?.abort();
      onOpenChange?.(nextOpen);
    },
  });

  const [draftSource, setDraftSource] = useState<DataWidgetSourceConfig>({
    mode: 'dynamic',
    strategy: 'newest',
    limit: 4,
    filters: {},
  });
  const [draftButtonSettings, setDraftButtonSettings] = useState<CollectionButtonSettings>(
    DEFAULT_COLLECTION_BUTTON_SETTINGS,
  );
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [addPageOpen, setAddPageOpen] = useState(false);

  const widgetType = isDataWidgetBlockType(block.blockType) ? block.blockType : 'blog_widget';
  const widgetLabel = getDataWidgetLabel(widgetType);
  const creationConfig = getWidgetCreationConfig(widgetType);
  const hasDynamicRecords = Boolean(block.props?.hasDynamicRecords);
  const isConnected = draftSource.mode === 'dynamic';

  const hiddenMetadataSlots =
    hasDynamicRecords && isConnected
      ? []
      : settings.draftFields
          .filter(
            (field: CollectionCardField) =>
              isDataWidgetMetadataSlot(widgetType, field.slot) &&
              !(widgetType === 'blog_widget' && field.slot === 'image'),
          )
          .map((field: CollectionCardField) => field.slot);

  useEffect(() => {
    if (!settings.open) return;
    const props = resolveDataWidgetProps(block);
    setDraftSource(props.source);
    setDraftButtonSettings(readCollectionButtonSettings(block.props as Record<string, unknown>));
    setFetchError(null);
  }, [settings.open, block]);

  const handleFieldsChange = (nextFields: CollectionCardField[]) => {
    settings.setDraftFields(nextFields);

    const batchedUpdates: Array<{ blockId: string; updates: Partial<PageBlock> }> = [];

    nextFields.forEach((field, index) => {
      if (field.blockId) {
        batchedUpdates.push({
          blockId: field.blockId,
          updates: { visible: field.enabled, position: index + 1 },
        });
      }
    });

    if (batchedUpdates.length > 0) {
      batchUpdateBlocks(sectionId, batchedUpdates);
    }
  };

  const handleFiltersChange = async (slot: string, selection: DataWidgetFilterSelection) => {
    const nextSource = {
      ...draftSource,
      filters: { ...draftSource.filters, [slot]: selection },
    };
    setDraftSource(nextSource);

    if (!hasDynamicRecords) {
      settings.patchBlockProps({ source: sourceToProps(nextSource) });
      return;
    }

    await applyDynamicItems(nextSource);
  };

  const fetchResolvedWidgetProps = useCallback(
    async (
      source: DataWidgetSourceConfig,
      signal: AbortSignal,
    ): Promise<Record<string, unknown>> => {
      const nextWidgetBlock: PageBlock = {
        ...block,
        props: { ...(block.props ?? {}), source: sourceToProps(source) },
      };
      const blocks = [
        nextWidgetBlock,
        ...(cardTemplateBlock ? [cardTemplateBlock] : []),
        ...cardTemplateChildren,
      ];
      const resolvedPropsById = await resolveBlockProps(
        blocks,
        pageId,
        signal,
        sectionTemplateCode,
      );
      return resolvedPropsById.get(block.id) ?? {};
    },
    [block, cardTemplateBlock, cardTemplateChildren, pageId, sectionTemplateCode],
  );

  const applyDynamicItems = async (nextSource: DataWidgetSourceConfig) => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setFetchError(null);
    setIsFetching(true);
    try {
      const resolvedProps = await fetchResolvedWidgetProps(nextSource, controller.signal);
      if (controller.signal.aborted) return;
      const items = Array.isArray(resolvedProps.items)
        ? (resolvedProps.items as Record<string, unknown>[])
        : [];
      settings.patchBlockProps({
        source: sourceToProps(nextSource),
        items,
        hasDynamicRecords: Boolean(resolvedProps.hasDynamicRecords),
      });
    } catch {
      if (controller.signal.aborted) return;
      setFetchError('Failed to load records. Please try again.');
    } finally {
      if (!controller.signal.aborted) setIsFetching(false);
    }
  };

  const checkDynamicRecordsRef = useRef<((signal: AbortSignal) => Promise<void>) | null>(null);
  checkDynamicRecordsRef.current = async (signal: AbortSignal) => {
    if (hasDynamicRecords) return;
    const resolvedProps = await fetchResolvedWidgetProps(
      resolveDataWidgetProps(block).source,
      signal,
    );
    if (resolvedProps.hasDynamicRecords) {
      settings.patchBlockProps({ hasDynamicRecords: true });
    }
  };

  useEffect(() => {
    if (!settings.open) return;
    const controller = new AbortController();
    checkDynamicRecordsRef.current?.(controller.signal).catch(() => {});
    return () => controller.abort();
  }, [settings.open]);

  const handleSourceChange = async (patch: Partial<DataWidgetSourceConfig>) => {
    const nextSource = { ...draftSource, ...patch };
    setDraftSource(nextSource);
    setFetchError(null);

    if (nextSource.mode !== 'dynamic') {
      const existingStaticItems = Array.isArray(block.props?.staticItems)
        ? (block.props.staticItems as Record<string, unknown>[])
        : [];
      if (existingStaticItems.length > 0) {
        settings.patchBlockProps({ source: sourceToProps(nextSource), items: [] });
        return;
      }

      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsFetching(true);
      try {
        const resolvedProps = await fetchResolvedWidgetProps(nextSource, controller.signal);
        if (controller.signal.aborted) return;
        const staticItems = Array.isArray(resolvedProps.staticItems)
          ? (resolvedProps.staticItems as Record<string, unknown>[])
          : [];
        settings.patchBlockProps({
          source: sourceToProps(nextSource),
          items: [],
          staticItems,
        });
      } catch {
        if (controller.signal.aborted) return;
        settings.patchBlockProps({ source: sourceToProps(nextSource), items: [] });
      } finally {
        if (!controller.signal.aborted) setIsFetching(false);
      }
      return;
    }

    if (!hasDynamicRecords) {
      settings.patchBlockProps({ source: sourceToProps(nextSource), items: [] });
      return;
    }

    await applyDynamicItems(nextSource);
  };

  const handleAddPageClick = () => {
    if (creationConfig.kind === 'link') {
      openWindow(creationConfig.href, '_blank', 'noopener');
      return;
    }
    settings.handleOpenChange(false);
    setAddPageOpen(true);
  };

  const debouncedPatchButtonSettings = useDebounce((next: CollectionButtonSettings) => {
    settings.patchBlockProps({ buttonSettings: next });
  }, 300);

  const handleButtonSettingsChange = useCallback(
    (patch: Partial<CollectionButtonSettings>) => {
      const next = { ...draftButtonSettings, ...patch };
      setDraftButtonSettings(next);
      debouncedPatchButtonSettings(next);
    },
    [draftButtonSettings, debouncedPatchButtonSettings],
  );

  const filterConfig =
    draftSource.mode === 'dynamic' ? getWidgetFilterConfig(widgetType) : { slots: [] };

  const showButtonTab =
    isConnected && settings.draftFields.some((f) => f.kind === 'button' && f.enabled);

  return (
    <>
      <SettingsPopoverShell
        open={settings.open}
        onOpenChange={settings.handleOpenChange}
        title="Collection Settings"
        trigger={
          <BlockActionButton
            icon={<Icon icon={faGear} />}
            label="Widget Settings"
            srLabel={`Edit ${widgetLabel} widget settings`}
            showLabel
            isVisible={isVisible}
            disabled={disabled}
            className={cn(settings.open && 'bg-primary/7')}
            onPointerDown={(event) => event.stopPropagation()}
          />
        }
        layoutContent={
          <LayoutTab
            variant={settings.draftVariant}
            variants={variants}
            onVariantChange={(key) => settings.handleVariantChange(key as DataWidgetVariant)}
          />
        }
        contentContent={
          <div className="space-y-5">
            <DataSourceSection
              widgetLabel={widgetLabel}
              creationConfig={creationConfig}
              hasDynamicRecords={hasDynamicRecords}
              isConnected={isConnected}
              isFetching={isFetching}
              fetchError={fetchError}
              draftSource={draftSource}
              strategyOptions={getStrategyOptions(widgetType)}
              onSourceChange={handleSourceChange}
              onAddPageClick={handleAddPageClick}
            />
            {settings.draftFields.length > 0 ? (
              <>
                {isConnected ? <Separator /> : null}
                <ContentTab
                  fields={settings.draftFields}
                  onFieldsChange={handleFieldsChange}
                  hiddenSlots={hiddenMetadataSlots}
                  disabledImageField={settings.activeVariantEntry?.renderer === 'accordion'}
                  fieldGroups={getWidgetFieldGroups(widgetType)}
                  description={`Choose which elements appear in your ${widgetLabel.toLowerCase()}:`}
                  metadataFilter={
                    isConnected
                      ? {
                          ...filterConfig,
                          filters: draftSource.filters,
                          onFiltersChange: handleFiltersChange,
                        }
                      : undefined
                  }
                />
              </>
            ) : null}
          </div>
        }
        backgroundContent={
          <BackgroundTab
            variantProps={settings.activeVariantProps}
            onVariantPropsChange={settings.handleVariantPropsChange}
          />
        }
        settingsContent={
          settings.activeVariantEntry ? (
            <SettingsTab
              variantProps={settings.activeVariantProps}
              renderer={settings.activeVariantEntry.renderer}
              cardAlign={settings.draftCardAlign}
              onVariantPropsChange={settings.handleVariantPropsChange}
              onCardAlignChange={settings.handleCardAlignChange}
              showBackgroundPosition={widgetType === 'blog_widget'}
              showImagePosition={settings.draftFields.some(
                (field: CollectionCardField) => field.kind === 'image',
              )}
              imageAspect={settings.imageAspect}
              onImageAspectChange={settings.handleImageAspectChange}
            />
          ) : (
            <MutedText>No settings available.</MutedText>
          )
        }
        buttonContent={
          showButtonTab ? (
            <ButtonTab
              buttonSettings={draftButtonSettings}
              onButtonSettingsChange={handleButtonSettingsChange}
            />
          ) : undefined
        }
      />
      {creationConfig.kind === 'page' ? (
        <AddPageModal
          open={addPageOpen}
          onOpenChange={setAddPageOpen}
          initialSidebarSelection={creationConfig.initialSidebarSelection}
          chainedChildPageType={creationConfig.chainedChildPageType}
          openInNewTab
          onSuccess={() => setAddPageOpen(false)}
        />
      ) : null}
    </>
  );
}

function DataSourceSection({
  widgetLabel,
  creationConfig,
  hasDynamicRecords,
  isConnected,
  isFetching,
  fetchError,
  draftSource,
  strategyOptions,
  onSourceChange,
  onAddPageClick,
}: {
  widgetLabel: string;
  creationConfig: ReturnType<typeof getWidgetCreationConfig>;
  hasDynamicRecords: boolean;
  isConnected: boolean;
  isFetching: boolean;
  fetchError: string | null;
  draftSource: DataWidgetSourceConfig;
  strategyOptions: ReadonlyArray<{ value: DataWidgetStrategy; label: string }>;
  onSourceChange: (patch: Partial<DataWidgetSourceConfig>) => void;
  onAddPageClick: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium">Connect to {widgetLabel}</p>
          <MutedText className="text-sm">{creationConfig.helperCopy}</MutedText>
        </div>
        <Switch
          disabled={isFetching}
          checked={isConnected}
          onCheckedChange={(checked) => onSourceChange({ mode: checked ? 'dynamic' : 'static' })}
        />
      </div>

      {isConnected && !hasDynamicRecords ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-md border border-dashed px-8 py-8 text-center">
          <div className="space-y-0.5">
            <MutedText className="text-sm font-medium">{creationConfig.emptyTitle}</MutedText>
            <MutedText className="text-sm">{creationConfig.emptyDescription}</MutedText>
          </div>
          <Button
            variant="link"
            className="gap-1.5 font-semibold no-underline hover:no-underline"
            onClick={onAddPageClick}
          >
            <Icon icon={faPlus} className="size-4" />
            {creationConfig.createLabel}
          </Button>
        </div>
      ) : null}

      {isConnected && hasDynamicRecords ? (
        <>
          {fetchError ? <DestructiveText className="text-sm">{fetchError}</DestructiveText> : null}
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm font-medium">Display</p>
              <Select
                disabled={isFetching}
                value={draftSource.strategy}
                onValueChange={(strategy) =>
                  onSourceChange({
                    strategy: strategy as DataWidgetSourceConfig['strategy'],
                  })
                }
              >
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[150]">
                  {strategyOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between gap-4">
              <p className="text-sm font-medium">Number of Items</p>
              <Select
                disabled={isFetching}
                value={String(draftSource.limit)}
                onValueChange={(limit) => onSourceChange({ limit: Number(limit) })}
              >
                <SelectTrigger className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[150]">
                  {[1, 2, 3, 4, 5, 6, 8, 10, 12].map((count) => (
                    <SelectItem key={count} value={String(count)}>
                      {count}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
